import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges
} from '@angular/core';
import { IKorviuInputErrorMessage, KorviuInputComponent } from '../korviu-input/korviu-input.component';
import { LowerCasePipe, NgClass } from '@angular/common';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { AbstractControl } from '@angular/forms';
import { ReplaySubject, takeUntil } from 'rxjs';
// @ts-ignore
import zxcvbn, { ZXCVBNResult } from 'zxcvbn';
import { warningsMap } from './core/warnings.map';
import { suggestionsMap } from './core/suggestions.map';
import { isPasswordSimilar } from './core/password-similarity';

// Very weak (0) and weak (1) passwords are rejected.
export const MIN_ACCEPTED_PASSWORD_SCORE = 2;
// Matches the backend (Django) password validators: minimum length and not entirely numeric.
export const MIN_PASSWORD_LENGTH = 8;

// Value of the `passwordIsWeak` control error when the backend answers ERR_WEAK_PASSWORD.
export interface IWeakPasswordError {
  reasons?: string[];
  message?: string;
}

const WEAK_PASSWORD_REASONS = new Map<string, string>([
  ['password_too_short', 'validation.passwordStrength.reasons.passwordTooShort'],
  ['password_too_common', 'validation.passwordStrength.reasons.passwordTooCommon'],
  ['password_entirely_numeric', 'validation.passwordStrength.reasons.passwordEntirelyNumeric'],
  ['password_too_similar', 'validation.passwordStrength.reasons.passwordTooSimilar']
]);

export function toWeakPasswordError(payload: { reasons?: unknown; message?: unknown } | undefined): IWeakPasswordError {
  return {
    reasons: Array.isArray(payload?.reasons) ? payload.reasons.filter(reason => typeof reason === 'string') : [],
    message: typeof payload?.message === 'string' ? payload.message : undefined
  };
}

@Component({
  selector: 'app-password-input',
  standalone: true,
  imports: [KorviuInputComponent, TranslatePipe, NzIconModule, NgClass, LowerCasePipe],
  templateUrl: './password-input.component.html',
  styleUrl: './password-input.component.scss'
})
export class PasswordInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() passwordControl: AbstractControl;
  @Input() passwordValid!: boolean;
  @Output() passwordValidChange: EventEmitter<boolean>;
  @Input() passwordRecommendationsAreVisible: boolean;
  @Output() passwordRecommendationsAreVisibleChange: EventEmitter<boolean>;
  @Input() showErrorMessagesUnderField: boolean;
  @Input() errorMessagesUnderField: IKorviuInputErrorMessage[];
  // Personal data (name, email) the password should not resemble.
  @Input() userInputs: string[] = [];

  public isPasswordVisible: boolean;
  public passwordStrengthResult: ZXCVBNResult;
  public passwordStrengthLabels: Map<number, string>;
  public suggestionsMap: typeof suggestionsMap = suggestionsMap;
  public readonly minPasswordLength = MIN_PASSWORD_LENGTH;
  public readonly personalDataIsAvailable = signal(false);
  public readonly isNotSimilar = signal(true);

  public get isPasswordValid(): boolean {
    return (
      this.passwordStrengthResult?.score >= MIN_ACCEPTED_PASSWORD_SCORE &&
      this.meetsMinLength &&
      this.isNotEntirelyNumeric &&
      this.isNotSimilar()
    );
  }

  public get meetsMinLength(): boolean {
    return Array.from(this.passwordControl?.value ?? '').length >= MIN_PASSWORD_LENGTH;
  }

  public get isNotEntirelyNumeric(): boolean {
    const value: string = this.passwordControl?.value ?? '';
    return value.length > 0 && !/^\p{Decimal_Number}+$/u.test(value);
  }

  // Backend ERR_WEAK_PASSWORD reasons in our own wording; its message only for codes we don't know.
  public get serverWeakPasswordMessages(): { key?: string; text?: string }[] {
    const error: IWeakPasswordError | true | null = this.passwordControl?.getError('passwordIsWeak');
    if (!error || error === true) return [];
    const messages = (error.reasons ?? []).map(reason =>
      WEAK_PASSWORD_REASONS.has(reason) ? { key: WEAK_PASSWORD_REASONS.get(reason) } : { text: error.message }
    );
    return messages.filter(
      (message, index) =>
        (message.key || message.text) &&
        messages.findIndex(other => other.key === message.key && other.text === message.text) === index
    );
  }

  public get passwordIsTooWeak(): boolean {
    return this.passwordStrengthResult?.score < MIN_ACCEPTED_PASSWORD_SCORE && this.passwordControl?.value?.length > 0;
  }

  public get passwordStrengthLocalizableLabel(): string {
    return this.passwordStrengthLabels.get(this.passwordStrengthResult?.score);
  }
  public get warningLocalizableLabel(): string {
    return warningsMap.get(this.passwordStrengthResult?.feedback?.warning || '');
  }
  public get suggestions(): string[] {
    return this.passwordStrengthResult?.feedback?.suggestions;
  }

  public get passwordIsVeryWeak(): boolean {
    return this.passwordStrengthResult?.score === 0;
  }
  public get passwordIsWeak(): boolean {
    return this.passwordStrengthResult?.score === 1;
  }
  public get passwordIsFair(): boolean {
    return this.passwordStrengthResult?.score === 2;
  }
  public get passwordIsStrong(): boolean {
    return this.passwordStrengthResult?.score === 3;
  }
  public get passwordIsVeryStrong(): boolean {
    return this.passwordStrengthResult?.score === 4;
  }

  private readonly _onDestroy$: ReplaySubject<void>;

  constructor() {
    this.showErrorMessagesUnderField = true;
    this.isPasswordVisible = false;
    this.passwordStrengthResult = null;
    this.passwordStrengthLabels = new Map<number, string>([
      [0, 'validation.passwordStrength.VERY_WEAK'],
      [1, 'validation.passwordStrength.WEAK'],
      [2, 'validation.passwordStrength.FAIR'],
      [3, 'validation.passwordStrength.STRONG'],
      [4, 'validation.passwordStrength.VERY_STRONG']
    ]);
    this.passwordValidChange = new EventEmitter<boolean>();
    this.passwordRecommendationsAreVisibleChange = new EventEmitter<boolean>();
    this._onDestroy$ = new ReplaySubject<void>(1);
  }

  public ngOnInit(): void {
    this.passwordControl.valueChanges.pipe(takeUntil(this._onDestroy$)).subscribe((value: string) => {
      this._evaluatePassword(value);
      this.passwordRecommendationsAreVisible = this.passwordControl.dirty && !!this.passwordControl.value;
      this.passwordRecommendationsAreVisibleChange.emit(this.passwordRecommendationsAreVisible);
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['userInputs'] && this.passwordStrengthResult) {
      // Name or email changed after the password was typed: re-score outside the current change detection pass.
      Promise.resolve().then(() => this._evaluatePassword(this.passwordControl.value));
    }
  }

  private _evaluatePassword(value: string): void {
    const userInputs = (this.userInputs ?? []).filter(input => typeof input === 'string' && input.trim().length > 0);
    this.personalDataIsAvailable.set(userInputs.length > 0);
    this.isNotSimilar.set(!isPasswordSimilar(value ?? '', userInputs));
    this.passwordStrengthResult = zxcvbn(value ?? '', userInputs);
    this.passwordValid = this.isPasswordValid;
    this.passwordValidChange.emit(this.isPasswordValid);
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
